import React from 'react';
import { LucideIcon, TrendingUp, TrendingDown } from "lucide-react";

interface MetricsCardProps {
  title: string;
  value: string | number;
  icon: LucideIcon;
  trend?: string;
  trendUp?: boolean;
  color?: string;
}

const MetricsCard = ({ 
  title, 
  value, 
  icon: Icon, 
  trend, 
  trendUp,
  color = '#067A76'
}: MetricsCardProps) => {
  // Extract percentage from trend string if present
  const getTrendPercentage = () => {
    if (!trend) return null;
    const match = trend.match(/[\d.]+%/);
    return match ? match[0] : null;
  };

  const trendPercentage = getTrendPercentage();

  // Generate color variations
  const hexToRgba = (hex: string, alpha: number) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  return (
    <div className="relative group">
      {/* Main Card */}
      <div 
        className="relative overflow-hidden rounded-xl sm:rounded-2xl bg-white border border-slate-100 p-3 sm:p-5 transition-all duration-300 hover:shadow-lg hover:border-slate-200"
        style={{
          boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.03)',
        }}
      >
        {/* Subtle gradient accent at top */}
        <div 
          className="absolute top-0 left-0 right-0 h-0.5 sm:h-1 opacity-80"
          style={{ 
            background: `linear-gradient(90deg, ${color}, ${hexToRgba(color, 0.6)})`,
          }}
        />

        {/* Soft background decoration */}
        <div 
          className="absolute -right-12 -top-12 sm:-right-16 sm:-top-16 w-28 h-28 sm:w-40 sm:h-40 rounded-full opacity-[0.04] group-hover:opacity-[0.08] transition-opacity duration-500"
          style={{ backgroundColor: color }}
        />

        {/* Content */}
        <div className="relative z-10">
          {/* Top row: Icon and Trend */}
          <div className="flex items-start justify-between mb-2 sm:mb-4">
            {/* Icon container */}
            <div 
              className="w-9 h-9 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl flex items-center justify-center transition-transform duration-300 group-hover:scale-105"
              style={{ 
                backgroundColor: hexToRgba(color, 0.1),
              }}
            >
              <Icon 
                className="h-4 w-4 sm:h-6 sm:w-6" 
                style={{ color }}
                strokeWidth={1.8}
              />
            </div>
            
            {/* Trend badge */}
            {trendPercentage && (
              <div 
                className={`flex items-center gap-0.5 sm:gap-1 px-1.5 sm:px-2.5 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-semibold ${
                  trendUp 
                    ? 'bg-emerald-50 text-emerald-600' 
                    : 'bg-rose-50 text-rose-500'
                }`}
              >
                {trendUp ? (
                  <TrendingUp className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
                ) : (
                  <TrendingDown className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
                )}
                <span className="hidden sm:inline">{trendUp ? '+' : ''}</span>{trendPercentage}
              </div>
            )}
          </div>

          {/* Title */}
          <p className="text-[10px] sm:text-sm font-medium text-slate-500 mb-0.5 sm:mb-1 truncate">
            {title}
          </p>

          {/* Value */}
          <h3 className="text-base sm:text-2xl font-bold text-slate-800 tracking-tight mb-1 sm:mb-2 truncate">
            {value}
          </h3>

          {/* Comparison text */}
          {trendPercentage && (
            <p className="text-[9px] sm:text-xs text-slate-400 flex items-center gap-1">
              <span className={trendUp ? 'text-emerald-500' : 'text-rose-500'}>
                {trend}
              </span>
              <span>vs last month</span>
            </p>
          )}
          {!trendPercentage && trend && (
            <p className="text-[9px] sm:text-xs text-slate-400 truncate">
              {trend}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default MetricsCard;
